import React, { useId } from "react";
import { Container } from "../../elements/Container";
import { SliderArrow } from "../../elements/SliderArrow";
import { styles } from "./TestimonialModule.style";
import { TestimonialItem } from "./TestimonialItem";
import type { TestimonialItemData } from "./TestimonialItem";
import { TestimonialStats } from "./TestimonialStats";
import type { TrustStat } from "./TestimonialStats";
import { useTestimonialCarousel } from "./utils";

export type { TestimonialItemData, TrustStat };

export interface TestimonialModuleProps {
  heading: string;
  headingLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  testimonials: [TestimonialItemData, ...TestimonialItemData[]];
  trustStats?: TrustStat[];
  initialIndex?: number;
  autoPlayInterval?: number;
  counterLabel?: (current: number, total: number) => string;
  prevAriaLabel?: string;
  nextAriaLabel?: string;
}

export const TestimonialModule = ({
  heading,
  headingLevel: HeadingTag = "h2",
  testimonials,
  trustStats,
  initialIndex = 0,
  autoPlayInterval = 5000,
  counterLabel = (current, total) => `${current} of ${total}`,
  prevAriaLabel = "Previous testimonial",
  nextAriaLabel = "Next testimonial",
}: TestimonialModuleProps) => {
  const headingId = useId();
  const total = testimonials.length;

  const {
    currentIndex,
    dragOffset,
    isDragging,
    isAtStart,
    isAtEnd,
    carouselRef,
    goTo,
    goNext,
    goPrev,
    pauseHandlers,
    pointerHandlers,
  } = useTestimonialCarousel({ total, initialIndex, autoPlayInterval });

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <div className={styles.headerRow}>
        <Container>
          <div className={styles.headerInner}>
            <HeadingTag id={headingId} className={styles.heading}>
              {heading}
            </HeadingTag>
            <div className={styles.controls} aria-label="Carousel controls">
              <p className={styles.counter} aria-live="polite" aria-atomic="true">
                {counterLabel(currentIndex + 1, total)}
              </p>
              <SliderArrow direction="left" onClick={goPrev} ariaLabel={prevAriaLabel} disabled={isAtStart} />
              <SliderArrow direction="right" onClick={goNext} ariaLabel={nextAriaLabel} disabled={isAtEnd} />
            </div>
          </div>
        </Container>
      </div>
      <div
        ref={carouselRef}
        className={styles.carouselOuter}
        {...pauseHandlers}
        {...pointerHandlers}
        data-dragging={isDragging || undefined}
      >
        <ul
          className={styles.track}
          style={
            {
              transform: `translateX(calc(-${currentIndex} * (var(--card-width) + var(--card-gap)) + ${dragOffset}px))`,
              transition: isDragging ? "none" : undefined,
            } as React.CSSProperties
          }
          aria-label={heading}
        >
          {testimonials.map((item, i) => (
            <TestimonialItem key={i} item={item} isHidden={i !== currentIndex} />
          ))}
        </ul>
      </div>
      <div className={styles.dotsRow}>
        <Container>
          <div className={styles.dotsInner}>
            <div className={styles.dots} role="group" aria-label="Testimonial navigation">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  className={styles.dot}
                  data-active={currentIndex === i || undefined}
                  onClick={() => goTo(i)}
                  aria-pressed={currentIndex === i}
                  type="button"
                />
              ))}
            </div>
            <p className={styles.counterMobile} aria-live="polite" aria-atomic="true">
              {counterLabel(currentIndex + 1, total)}
            </p>
          </div>
        </Container>
      </div>
      {trustStats && trustStats.length > 0 && <TestimonialStats stats={trustStats} />}
    </section>
  );
};
